import { Colors, Lightning, Storage } from "@lightningjs/sdk";
import { TranslatableText } from "../../../../../components/data-display/TranslatableText/TranslatableText";
import { Button } from "../../../../../components/input/Button/Button";
import theme from "../../../../../lib/theme";
import { STORAGE_KEYS } from "../../../../../lib/utils";

const AGE_OPTIONS = [0, 6, 10, 12, 16, 18];
const AGE_BOX_W = 70;
const AGE_BOX_H = 50;
const AGE_BOX_SPACING = 12;

interface SettingsAgeContentTemplateSpec extends Lightning.Component.TemplateSpec {
    Title: typeof TranslatableText;
    AgeButtons: object;
    OkWrapper: {
        Border: object;
        OkBtn: typeof Button;
    };
    SuccessView: {
        Icon: object;
        Message: typeof TranslatableText;
    };
}

export class SettingsAgeContent
    extends Lightning.Component<SettingsAgeContentTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<SettingsAgeContentTemplateSpec>
{
    private _selectedAgeIndex: number = 4;
    private _onConfirm?: (age: string) => void;

    readonly Title = this.getByRef("Title")!;
    readonly AgeButtons = this.getByRef("AgeButtons")!;
    readonly OkWrapper = this.getByRef("OkWrapper")!;
    readonly OkBtn = this.OkWrapper.getByRef("OkBtn")!;
    readonly SuccessView = this.getByRef("SuccessView")!;

    static override _template(): Lightning.Component.Template<SettingsAgeContentTemplateSpec> {
        const totalButtonsWidth = AGE_BOX_W * AGE_OPTIONS.length + AGE_BOX_SPACING * (AGE_OPTIONS.length - 1);

        return {
            w: 440,
            h: theme.layout.h - 150,
            flex: {
                direction: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                paddingTop: 40
            },
            Title: {
                flexItem: { marginBottom: 28 },
                type: TranslatableText,
                key: "settings.ageRestriction",
                text: {
                    textAlign: "left",
                    fontFace: "SemiBold",
                    fontSize: 28,
                    textColor: Colors(theme.colors.text.primary).get(),
                    wordWrap: true,
                    wordWrapWidth: 400
                }
            },
            AgeButtons: {
                flexItem: { marginBottom: 0 },
                w: totalButtonsWidth,
                h: AGE_BOX_H,
                flex: { direction: "row", alignItems: "center" },
                ...Object.fromEntries(
                    AGE_OPTIONS.map((age, i) => [
                        `Age${i}`,
                        {
                            w: AGE_BOX_W,
                            h: AGE_BOX_H,
                            flexItem: i < AGE_OPTIONS.length - 1 ? { marginRight: AGE_BOX_SPACING } : {},
                            Background: {
                                x: 0,
                                y: 0,
                                texture: Lightning.Tools.getRoundRect(
                                    AGE_BOX_W - 2, AGE_BOX_H - 2, 8, 2, 0xffffffff, false, 0x00000000
                                )
                            },
                            Label: {
                                x: AGE_BOX_W / 2,
                                y: AGE_BOX_H / 2,
                                mountX: 0.5,
                                mountY: 0.5,
                                text: {
                                    text: `${age}+`,
                                    fontSize: 22,
                                    fontFace: "Regular",
                                    textColor: 0xffffffff,
                                    textAlign: "center"
                                }
                            }
                        }
                    ])
                )
            },
            OkWrapper: {
                flexItem: false,
                x: 0,
                y: (theme.layout.h - 150) - 40 - ((theme.button.h || 56) + 2),
                w: 300,
                h: (theme.button.h || 56) + 2,
                Border: {
                    x: -1,
                    y: -1,
                    w: 302,
                    h: (theme.button.h || 56) + 2,
                    rect: true,
                    color: 0x00000000,
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: theme.button.radius || 8,
                        stroke: 2,
                        strokeColor: 0xffffffff
                    }
                },
                OkBtn: {
                    type: Button,
                    w: 300,
                    label: "Ok",
                    actionId: "ageOk"
                }
            },
            SuccessView: {
                alpha: 0,
                visible: false,
                flex: {
                    direction: "column",
                    alignItems: "center",
                    justifyContent: "center"
                },
                w: 440,
                flexItem: { marginTop: 40 },
                Icon: {
                    w: 80,
                    h: 80,
                    Circle: {
                        w: 80,
                        h: 80,
                        rect: true,
                        color: 0xffffffff,
                        shader: {
                            type: Lightning.shaders.RoundedRectangle,
                            radius: 40
                        }
                    },
                    Check: {
                        x: 14,
                        y: 14,
                        zIndex: 2,
                        w: 52,
                        h: 52
                    }
                },
                Message: {
                    flexItem: { marginTop: 20 },
                    type: TranslatableText,
                    key: "pin.ageChangeSuccess",
                    text: {
                        textAlign: "center",
                        fontSize: 24,
                        textColor: Colors(theme.colors.text.primary).get(),
                        wordWrap: true,
                        wordWrapWidth: 400
                    }
                }
            }
        };
    }

    override _init() {
        this._setState("AgeButtonsState");
    }

    static override _states() {
        return [
            class AgeButtonsState extends this {
                override _getFocused(): any {
                    return this;
                }

                override _handleLeft() {
                    if (this._selectedAgeIndex > 0) {
                        this._selectedAgeIndex--;
                        this._updateAgeSelection();
                    }
                    return true;
                }

                override _handleRight() {
                    if (this._selectedAgeIndex < AGE_OPTIONS.length - 1) {
                        this._selectedAgeIndex++;
                        this._updateAgeSelection();
                    }
                    return true;
                }

                override _handleDown() {
                    this._setState("OkState");
                    return true;
                }

                override _handleEnter() {
                    this._setState("OkState");
                    return true;
                }

                override _handleBack() {
                    this._returnFocusToList();
                    return true;
                }
            },
            class OkState extends this {
                override _getFocused() {
                    return this.OkBtn;
                }

                override _handleUp() {
                    this._setState("AgeButtonsState");
                    return true;
                }

                override _handleBack() {
                    this._returnFocusToList();
                    return true;
                }
            }
        ];
    }

    _returnFocusToList() {
        this.fireAncestors("$unfocusContent");
    }

    $handleBtnClick(id: string) {
        if (id === "ageOk") {
            this._confirmAge();
        }
    }

    open(onConfirm: (age: string) => void, currentAge?: string) {
        this._onConfirm = onConfirm;

        // Determine initial selection from current stored age
        if (currentAge) {
            const ageNum = parseInt(currentAge, 10);
            const idx = AGE_OPTIONS.indexOf(ageNum);
            this._selectedAgeIndex = idx >= 0 ? idx : 4;
        } else {
            // Try to load from storage
            const user = Storage.get(STORAGE_KEYS.user) as any;
            const storedAge = user?.settings?.parentalAgeRestriction || Storage.get(STORAGE_KEYS.parentalAgeRestriction);
            if (storedAge) {
                const ageNum = parseInt(storedAge, 10);
                const idx = AGE_OPTIONS.indexOf(ageNum);
                this._selectedAgeIndex = idx >= 0 ? idx : 4;
            } else {
                this._selectedAgeIndex = 4;
            }
        }

        this._hideSuccess();
        this._updateAgeSelection();
        this._updateTitle();
        this._setState("AgeButtonsState");
    }

    _updateAgeSelection() {
        for (let i = 0; i < AGE_OPTIONS.length; i++) {
            // @ts-ignore dynamic ref access
            const ageBox = this.AgeButtons.getByRef(`Age${i}`) as any;
            if (ageBox) {
                const isSelected = i === this._selectedAgeIndex;
                const bg = ageBox.getByRef("Background") as any;
                if (bg) {
                    if (isSelected) {
                        bg.texture = Lightning.Tools.getRoundRect(
                            AGE_BOX_W - 2, AGE_BOX_H - 2, 8, 0, 0x00000000, true, 0xffffffff
                        );
                    } else {
                        bg.texture = Lightning.Tools.getRoundRect(
                            AGE_BOX_W - 2, AGE_BOX_H - 2, 8, 2, 0xffffffff, false, 0x00000000
                        );
                    }
                }
                ageBox.patch({
                    smooth: { scale: isSelected ? 1.15 : 1.0 }
                });
                const label = ageBox.getByRef("Label") as any;
                if (label) {
                    label.patch({
                        text: {
                            fontFace: "SemiBold",
                            fontSize: isSelected ? 24 : 22,
                            textColor: isSelected ? 0xff1565c0 : 0xffffffff
                        }
                    });
                }
            }
        }
        this._updateTitle();
    }

    _updateTitle() {
        const age = AGE_OPTIONS[this._selectedAgeIndex];
        this.Title.patch({
            text: {
                text: `Altersfreigabe (${age}+)`
            }
        } as any);
    }

    _confirmAge() {
        const age = AGE_OPTIONS[this._selectedAgeIndex]!;
        const ageStr = age < 10 ? `0${age}` : `${age}`;
        this._onConfirm?.(ageStr);
        this._showSuccess();
    }

    _showSuccess() {
        this.Title.patch({ alpha: 0 });
        this.AgeButtons.patch({ alpha: 0, visible: false });
        this.OkWrapper.patch({ alpha: 0, visible: false });

        const iconContainer = this.SuccessView.getByRef("Icon") as any;
        if (iconContainer) {
            const checkEl = iconContainer.getByRef("Check") as any;
            if (checkEl) {
                const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="52" height="52"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#1565C0"/></svg>`;
                const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
                checkEl.patch({
                    texture: Lightning.Tools.getSvgTexture(dataUrl, 52, 52)
                });
            }
        }

        this.SuccessView.patch({ alpha: 1, visible: true });
    }

    _hideSuccess() {
        this.SuccessView.patch({ alpha: 0, visible: false });
        this.Title.patch({ alpha: 1 });
        this.AgeButtons.patch({ alpha: 1, visible: true });
        this.OkWrapper.patch({ alpha: 1, visible: true });
    }
}
